import React from 'react';
import { Link, type LinkProps } from 'react-router';

import useReactRoutes from 'javascripts/hooks/useReactRoutes';
import styled, { css } from 'styled-components';

type Variant = 'primary' | 'breadcrumb';

type BackwardsCompatibleLinkProps = {
  to: string;
  children: React.ReactNode;
  anchorTagProps?: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;
  className?: string;
  'data-intercom-target'?: string;
  'data-testid'?: string;
  linkProps?: Omit<LinkProps, 'to'>;
  onClick?: (e?: React.MouseEvent<HTMLAnchorElement>) => void;
  variant?: Variant;
};

const breadcrumbStyleString = `
  color: var(--cc_color_link_subtle);
  font-weight: 700;
  font-size: calc(var(--cc_size_text_s) + 1px);
  padding: 0px 0.25ch;
  font-family: Averta, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Arial, sans-serif;
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_xxs)
`;

const breadcrumbStyles = css`
  ${breadcrumbStyleString}
`;

export const StyledLink = styled(Link)<{ variant: Variant }>`
  ${({ variant }) => variant === 'breadcrumb' && breadcrumbStyles}
`;

function BackwardsCompatibleLink({
  to,
  children,
  className,
  'data-intercom-target': dataIntercomTarget,
  'data-testid': dataTestId,
  onClick = () => {},
  linkProps,
  anchorTagProps,
  variant = 'primary',
}: BackwardsCompatibleLinkProps) {
  const { isReactAppMounted } = useReactRoutes();

  if (isReactAppMounted) {
    return (
      <StyledLink
        to={to}
        className={className}
        onClick={onClick}
        data-testid={dataTestId}
        data-intercom-target={dataIntercomTarget}
        variant={variant}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...linkProps}
      >
        {children}
      </StyledLink>
    );
  }

  return (
    <a
      data-turbo="false"
      href={to}
      className={className}
      // @ts-expect-error - we want to manually apply the style when we have breadcrumb variant for rails views because the styled component is not available
      style={{ cssText: variant === 'breadcrumb' ? breadcrumbStyleString : '' }}
      onClick={onClick}
      data-testid={dataTestId}
      data-intercom-target={dataIntercomTarget}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...anchorTagProps}
    >
      {children}
    </a>
  );
}

export default BackwardsCompatibleLink;
